import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ComicCard from '@/components/base/ComicCard';
import { allComics } from '@/mocks/comics';
import type { Chapter, Comic } from '@/mocks/comics';

function generateChapters(comic: Comic): Chapter[] {
  if (comic.chapters && comic.chapters.length > 0) return comic.chapters;

  const latestNum = parseInt(comic.latestChapter.replace(/[^0-9]/g, '')) || 100;
  const chapters: Chapter[] = [];
  const totalChapters = comic.status === '已完结' ? Math.min(latestNum, 320) : Math.min(latestNum, 320);

  for (let i = totalChapters; i >= 1; i--) {
    chapters.push({
      id: i,
      title: `第${i}话`,
      updateTime: i === totalChapters ? comic.updateTime : `2026-0${Math.ceil(Math.random() * 7 + 1)}-${String(Math.floor(Math.random() * 28) + 1).padStart(2, '0')}`,
    });
  }
  return chapters;
}

export default function ComicDetail() {
  const { id } = useParams<{ id: string }>();
  const [scrollToTopVisible, setScrollToTopVisible] = useState(false);
  const comic = allComics.find((c) => c.id === Number(id));

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [id]);

  useEffect(() => {
    const handleScroll = () => {
      setScrollToTopVisible(window.scrollY > 600);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (!comic) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16 flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-emotion-sad-line text-3xl text-foreground-300"></i>
            </div>
            <h1 className="text-xl font-heading font-bold text-foreground-900 mb-2">漫画未找到</h1>
            <p className="text-foreground-500 mb-6">该漫画不存在或已被移除</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-background-50 dark:text-foreground-950 rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer text-sm"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const chapters = generateChapters(comic);
  const relatedComics = allComics
    .filter((c) => c.id !== comic.id && (c.categorySlug === comic.categorySlug || c.tags.some((t) => comic.tags.includes(t))))
    .slice(0, 6);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <main className="pt-16">
        {/* Breadcrumb */}
        <div className="bg-background-100/70 border-b border-background-200/60">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-3">
            <nav aria-label="面包屑导航" className="flex items-center gap-2 text-sm flex-wrap">
              <Link to="/" className="text-foreground-500 hover:text-primary-500 transition-colors cursor-pointer whitespace-nowrap">
                首页
              </Link>
              <i className="ri-arrow-right-s-line text-foreground-400 text-xs"></i>
              <Link to={`/category/${comic.categorySlug}`} className="text-foreground-500 hover:text-primary-500 transition-colors cursor-pointer whitespace-nowrap">
                {comic.category}
              </Link>
              <i className="ri-arrow-right-s-line text-foreground-400 text-xs"></i>
              <span className="text-foreground-800 font-medium line-clamp-1">{comic.title}</span>
            </nav>
          </div>
        </div>

        {/* Comic Info */}
        <section className="py-8 md:py-12 bg-background-50">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="flex flex-col md:flex-row gap-8">
              <div className="flex-shrink-0 mx-auto md:mx-0">
                <div className="w-48 md:w-56 aspect-[3/4] rounded-xl overflow-hidden shadow-sm">
                  <img
                    src={comic.cover}
                    alt={comic.title}
                    title={`${comic.title} - 妖精漫画网免费漫画在线阅读`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              <div className="flex-1">
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">
                  {comic.title}
                </h1>
                <div className="flex flex-wrap items-center gap-3 text-sm text-foreground-600 mb-4">
                  <span className="flex items-center gap-1">
                    <i className="ri-user-line"></i> 作者：{comic.author}
                  </span>
                  <span className="text-background-300">|</span>
                  <Link
                    to={`/category/${comic.categorySlug}`}
                    className="flex items-center gap-1 text-primary-500 hover:text-primary-600 transition-colors cursor-pointer"
                  >
                    <i className="ri-folder-line"></i> {comic.category}
                  </Link>
                  <span className="text-background-300">|</span>
                  <span className={`flex items-center gap-1 ${comic.status === '连载中' ? 'text-accent-600' : 'text-secondary-600'}`}>
                    <i className={comic.status === '连载中' ? 'ri-loop-right-line' : 'ri-check-double-line'}></i>
                    {comic.status}
                  </span>
                </div>

                <div className="flex flex-wrap items-center gap-3 mb-5">
                  <div className="flex items-center gap-1.5 bg-accent-50 px-3 py-1.5 rounded-full">
                    <i className="ri-star-fill text-accent-500 text-sm"></i>
                    <span className="text-sm font-semibold text-accent-700">{comic.rating}</span>
                    <span className="text-xs text-accent-500">分</span>
                  </div>
                  <div className="flex items-center gap-1.5 bg-background-100 px-3 py-1.5 rounded-full">
                    <i className="ri-eye-line text-foreground-400 text-sm"></i>
                    <span className="text-sm text-foreground-600">{comic.views}</span>
                  </div>
                  <div className="flex items-center gap-1.5 bg-background-100 px-3 py-1.5 rounded-full">
                    <i className="ri-time-line text-foreground-400 text-sm"></i>
                    <span className="text-sm text-foreground-600">更新于 {comic.updateTime}</span>
                  </div>
                </div>

                <div className="flex flex-wrap gap-1.5 mb-5">
                  {comic.tags.map((tag) => (
                    <span key={tag} className="text-xs px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-700 whitespace-nowrap">
                      {tag}
                    </span>
                  ))}
                </div>

                <p className="text-sm text-foreground-700 leading-relaxed mb-6 bg-background-100/70 rounded-xl p-4 border border-background-200/60">
                  {comic.description || `${comic.title}是${comic.author}创作的一部精彩漫画作品，收录于妖精漫画网${comic.category}分类。该作品当前状态为${comic.status}，最新章节为${comic.latestChapter}。妖精漫画网为您提供${comic.title}全集的免费在线阅读服务，下拉式阅读体验超爽。`}
                </p>

                <div className="flex flex-wrap gap-3">
                  <Link
                    to={`/comic/${comic.id}/read/1`}
                    className="px-8 py-3 bg-primary-500 text-background-50 dark:text-foreground-950 font-medium rounded-full hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer text-sm"
                  >
                    开始阅读 <i className="ri-play-line ml-1"></i>
                  </Link>
                  <button className="px-8 py-3 bg-background-100 text-foreground-700 font-medium rounded-full hover:bg-background-200 transition-all whitespace-nowrap cursor-pointer text-sm border border-background-200/60">
                    <i className="ri-bookmark-line mr-1"></i> 加入收藏
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Chapter List */}
        <section className="py-8 md:py-12 bg-background-100/70" aria-labelledby="chapters-heading">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-6">
              <h2 id="chapters-heading" className="text-xl md:text-2xl font-heading font-bold text-foreground-950">
                章节列表
                <span className="text-sm font-normal text-foreground-500 ml-2 font-body">
                  共 {chapters.length} 话
                </span>
              </h2>
              <div className="flex items-center gap-2">
                <button className="px-4 py-1.5 text-sm rounded-full bg-background-50 text-foreground-600 border border-background-200/60 hover:bg-background-200 transition-colors whitespace-nowrap cursor-pointer">
                  正序
                </button>
                <button className="px-4 py-1.5 text-sm rounded-full bg-secondary-500 text-background-50 dark:text-foreground-950 font-medium whitespace-nowrap cursor-pointer">
                  倒序
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
              {chapters.slice(0, 40).map((ch) => (
                <Link
                  key={ch.id}
                  to={`/comic/${comic.id}/read/${ch.id}`}
                  className="flex items-center justify-between px-4 py-2.5 rounded-lg bg-background-50 hover:bg-primary-50 transition-colors cursor-pointer border border-background-200/60 text-sm"
                >
                  <span className="text-foreground-800 line-clamp-1">{ch.title}</span>
                  <span className="text-xs text-foreground-400 whitespace-nowrap ml-2">{ch.updateTime}</span>
                </Link>
              ))}
            </div>

            {chapters.length > 40 && (
              <div className="text-center mt-6">
                <button className="px-6 py-2.5 text-sm bg-background-50 text-foreground-600 rounded-full border border-background-200/60 hover:bg-background-200 transition-colors whitespace-nowrap cursor-pointer">
                  展开全部章节 <i className="ri-arrow-down-s-line ml-1"></i>
                </button>
              </div>
            )}
          </div>
        </section>

        {/* Related Comics */}
        {relatedComics.length > 0 && (
          <section className="py-10 md:py-14 bg-background-50" aria-labelledby="related-heading">
            <div className="max-w-[1280px] mx-auto px-4 md:px-6">
              <h2 id="related-heading" className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-6">
                猜你喜欢
              </h2>
              <ComicCard comic={relatedComics[0]} size="medium" />
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
                {relatedComics.map((c) => (
                  <ComicCard key={c.id} comic={c} size="medium" />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />

      {scrollToTopVisible && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-8 right-8 z-40 w-11 h-11 flex items-center justify-center rounded-full bg-primary-500 text-white dark:text-foreground-950 shadow-lg hover:bg-primary-600 transition-all cursor-pointer"
          aria-label="回到顶部"
        >
          <i className="ri-arrow-up-line text-lg"></i>
        </button>
      )}
    </div>
  );
}